import { createContext, useContext, useRef, useState, useCallback, useMemo } from 'react';

const PageChromeContext = createContext(null);

/**
 * Slots in the sticky Layout header that PageHeading fills via portal.
 */
export function PageChromeProvider({ children }) {
  const mainRef = useRef(null);
  const subNavRef = useRef(null);
  const tabsRef = useRef(null);
  const [slotTick, setSlotTick] = useState(0);
  const [hasHeading, setHasHeading] = useState(false);

  const bump = useCallback(() => setSlotTick((v) => v + 1), []);

  const setSubNavNode = useCallback(
    (node) => {
      if (subNavRef.current === node) return;
      subNavRef.current = node;
      bump();
    },
    [bump]
  );

  const setMainNode = useCallback(
    (node) => {
      if (mainRef.current === node) return;
      mainRef.current = node;
      bump();
    },
    [bump]
  );

  const setTabsNode = useCallback(
    (node) => {
      if (tabsRef.current === node) return;
      tabsRef.current = node;
      bump();
    },
    [bump]
  );

  const value = useMemo(
    () => ({
      mainRef,
      subNavRef,
      tabsRef,
      setSubNavNode,
      setMainNode,
      setTabsNode,
      slotTick,
      bump,
      hasHeading,
      setHasHeading,
    }),
    [setSubNavNode, setMainNode, setTabsNode, slotTick, bump, hasHeading]
  );

  return <PageChromeContext.Provider value={value}>{children}</PageChromeContext.Provider>;
}

export function usePageChrome() {
  const ctx = useContext(PageChromeContext);
  if (!ctx) {
    throw new Error('usePageChrome must be used within PageChromeProvider');
  }
  return ctx;
}

/** Safe for pages outside Layout (e.g. login) ?? returns null. */
export function usePageChromeOptional() {
  return useContext(PageChromeContext);
}
